<template>
  <div>
    <v-tabs v-model="selectedTab">
      <v-tab>Intro</v-tab>
      <v-tab v-for="(question, index) in questions" :key="'q-tab-' + index">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <span v-on="on">Question {{ index + 1 }}</span>
          </template>
          {{ question.label }}
        </v-tooltip>
      </v-tab>
      <v-tab>Completion</v-tab>
      <v-spacer></v-spacer>
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" icon @click="addQuestion">
            <v-icon>fal fa-plus</v-icon>
          </v-btn>
        </template>
        <span>Add a question</span>
      </v-tooltip>
    </v-tabs>
    <v-tabs-items v-model="selectedTab">
      <v-tab-item>
        <v-card>
          <v-card-text>
            <v-text-field v-model="name" label="Name of block (for administrative purposes; optional)"></v-text-field>
            <p>Please enter the text that will be shown to introduce the quiz. A "Begin" button will be placed beneath this text to start the quiz.</p>
            <html-editor v-model="content"></html-editor>
            <v-switch v-model="mustComplete" :label="mustCompleteLabel"></v-switch>
            <v-switch v-model="showAnswer" :label="showAnswerLabel"></v-switch>
            <v-switch v-if="showAnswer" v-model="correctToProceed" :label="correctToProceedLabel"></v-switch>
            <v-switch v-model="oversized" :label="oversizedLabel">
              <template v-slot:prepend>
                <v-dialog v-model="oversizedDialog" width="400">
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon>
                      <v-icon color="info">fas fa-question-circle</v-icon>
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-title>Oversized Quiz</v-card-title>
                    <v-card-text>
                      <p>A quiz is set up so that each page of the quiz (intro text, question, final text) are all the same height. This option allows you to increase that height by 50%. This is good if you have a lot of long questions, as it will reduce the need for the student to scroll within the card itself but may require scrolling the page. You can test it out with your quiz to determine which option works best.</p>
                      <p>Make sure you test it out either with your phone or with your browser set to a smaller width to see how it will work on a mobile device.</p>
                    </v-card-text>
                    <v-card-actions>
                      <v-btn text @click="oversizedDialog = false">Close</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </template>
            </v-switch>
            <!-- <v-switch v-model="mathQuiz" :label="mathQuizLabel"></v-switch> -->
          </v-card-text>
        </v-card>
      </v-tab-item>
      <v-tab-item v-for="(question, index) in questions" :key="'q-content-' + index">
        <question
          :question="question"
          :math-quiz="mathQuiz"
          @change="(question) => updateQuestion(index, question)"
          @remove="removeQuestion(index)"></question>
      </v-tab-item>
      <v-tab-item>
        <v-card>
          <v-card-text>Please enter the text that will be shown at the conclusion of the quiz. You can include the answer from any question using the {} in the toolbar.</v-card-text>
          <html-editor v-model="finalText" :merge-fields="questionsForMerge"></html-editor>
          <quiz-stats :id="value._id"></quiz-stats>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>
<script>
import { ref, computed, watch } from '@vue/composition-api'
export default {
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  components: {
    HtmlEditor: () => import('@/components/forms/HTMLEditor'),
    Question: () => import('@/components/admin/editor/quiz/question'),
    QuizStats: () => import('@/components/admin/editor/quiz/stats')
  },
  setup (props, { root, emit }) {
    const name = ref('')
    const selectedTab = ref(0)
    const content = ref(props.value.content || '')

    const mustComplete = ref('mustComplete' in props.value ? props.value.mustComplete : false)
    const mustCompleteLabel = computed(() => mustComplete.value ? 'Quiz must be completed to move to next slide' : 'Quiz is optional and can be left incomplete')

    const showAnswer = ref('showAnswer' in props.value ? props.value.showAnswer : true)
    const showAnswerLabel = computed(() => showAnswer.value ? 'Show whether answer was correct or incorrect immediately' : 'Do not show whether the answer was correct or incorrect')

    const correctToProceed = ref('correctToProceed' in props.value ? props.value.correctToProceed : false)
    const correctToProceedLabel = computed(() => correctToProceed.value ? 'Each question must be answered correctly before moving to the next question' : 'The user gets one chance to answer the question then the question is disabled so they cannot change it')

    const oversized = ref('oversized' in props.value ? props.value.oversized : false)
    const oversizedLabel = computed(() => oversized.value ? 'Oversized: True. The quiz card will be 50% taller than the default.' : 'Oversized: False. The quiz card will be the standard height.')
    const oversizedDialog = ref(false)

    const mathQuiz = ref('mathQuiz' in props.value ? props.value.mathQuiz : false)
    const mathQuizLabel = computed(() => mathQuiz.value ? 'Use math editor in quiz questions (allows for formulas, but fewer styling options)' : 'Use text editor in quiz questions (better styling options, but unable to do math formulas)')

    const questions = ref(props.value.questions || [])
    const finalText = ref(props.value.finalText || '')

    const questionsForMerge = computed(() => {
      let arr = [{ label: 'Quiz Score', subtext: 'i.e. "4/6 Correct"' }, { label: 'Quiz Grade', subtext: 'i.e. "50% Correct"' }]
      for (let i = 0; i < questions.value.length; i++) {
        arr.push({ label: questions.value[i].label, subtext: 'Will show their selected answer' })
      }
      return arr
    })

    watch(() => props.value, () => {
      if (props.value.name !== name.value) name.value = props.value.name
      if (props.value.content !== content.value) content.value = props.value.content
      if (props.value.mustComplete !== mustComplete.value) mustComplete.value = props.value.mustComplete
      if (props.value.correctToProceed !== correctToProceed.value) correctToProceed.value = props.value.correctToProceed
      if (props.value.oversized !== oversized.value) oversized.value = props.value.oversized
      if (props.value.finalText !== finalText.value) finalText.value = props.value.finalText
      if (JSON.stringify(props.value.questions) !== JSON.stringify(questions.value)) questions.value = props.value.questions
    })

    watch(name, () => name.value !== props.value.name ? emit('update', { name: name.value }) : '')
    watch(content, () => content.value !== props.value.content ? emit('update', { content: content.value }) : '')
    watch(mustComplete, () => mustComplete.value !== props.value.mustComplete ? emit('update', { mustComplete: mustComplete.value }) : '')
    watch(correctToProceed, () => correctToProceed.value !== props.value.correctToProceed ? emit('update', { correctToProceed: correctToProceed.value }) : '')
    watch(oversized, () => oversized.value !== props.value.oversized ? emit('update', { oversized: oversized.value }) : '')
    watch(questions, () => questions.value !== props.value.questions ? emit('update', { questions: questions.value }) : '')
    watch(finalText, () => finalText.value !== props.value.finalText ? emit('update', { finalText: finalText.value }) : '')

    function addQuestion () {
      questions.value.push({ label: '', type: 'Radio', answers: [{ text: '', response: '' }] })
      root.$nextTick(() => { selectedTab.value = questions.value.length + 1 })
    }

    function updateQuestion (index, question) {
      questions.value.splice(index, 1, question)
    }

    function removeQuestion (index) {
      questions.value.splice(index, 1)
    }

    function validate () {
      return true
    }

    return {
      name,
      selectedTab,
      content,
      mustComplete,
      mustCompleteLabel,
      showAnswer,
      showAnswerLabel,
      correctToProceed,
      correctToProceedLabel,
      oversized,
      oversizedLabel,
      oversizedDialog,
      mathQuiz,
      mathQuizLabel,
      questions,
      finalText,
      questionsForMerge,
      addQuestion,
      updateQuestion,
      removeQuestion,
      validate
    }
  }
}
</script>
